import { useTranslation } from 'react-i18next';
import { Alert } from '@/shared/ui';
import { useAgendaParams } from '@/shared/navigation/react/useAgendaParams';
import { DETAIL_QUERY_STALE_TIME_MS } from '@/shared/lib/queryClient';
import { useGetVenuaPlannerProduct } from '@/api/generated/venua-planner-planning/venua-planner-planning';
import { productDetailAgenda } from './productDetailAgenda';
import { ProductDetailView } from './ProductDetailView';
import { fromGetProductResponse } from './productDetailFormSchema';

interface ProductDetailPageProps {
  layerId: string;
}

/**
 * Thin loader detailu produktu: vyřeší parametr agendy a načtení detailu. View vykreslí až s finálními
 * výchozími hodnotami. Krátký `staleTime` — detail se při každém otevření načte znovu (viz GUIDELINES
 * „Detail stránky — čerstvost dat").
 */
export function ProductDetailPage({ layerId }: ProductDetailPageProps) {
  const { t } = useTranslation('venua-planner');
  const { open } = useAgendaParams(productDetailAgenda, layerId);
  const productId = open.productId;

  const productQuery = useGetVenuaPlannerProduct(productId, {
    query: { staleTime: DETAIL_QUERY_STALE_TIME_MS },
  });

  if (productQuery.isLoading) {
    return (
      <p className="text-phx-text-muted text-phx-sm p-phx-5" data-testid="product-detail">
        {t('productDetail.loading')}
      </p>
    );
  }

  if (productQuery.isError || !productQuery.data) {
    return (
      <div className="p-phx-5" data-testid="product-detail">
        <Alert severity="error" title={t('productDetail.loadErrorTitle')}>
          {t('productDetail.loadErrorDescription')}
        </Alert>
      </div>
    );
  }

  return (
    <ProductDetailView
      layerId={layerId}
      productId={productId}
      initialValues={fromGetProductResponse(productQuery.data)}
    />
  );
}
